import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '../../../../../../core/auth';
import { useAppDispatch, useAppSelector } from '../../../../../../lib/store';
import { selectBatchId } from '../../../../../supervisor-queue/state/selector';
import { BASE_ATTRIBUTE_NAMES } from '../../../../../workspace-common/constants';
import type { AttributeValue } from '../../../../../workspace-common/types/responseTypes';
import {
  useDeleteResolvedAnnotationMutation,
  useGetSupervisorWorkspaceQuery,
} from '../../../../api/supervisorApiSlice';
import { useSupervisorMode } from '../../../../context/SupervisorModeContext';
import {
  selectAnnotationIdsInEditMode,
  selectResolvedAllConceptsExpanded,
  selectResolvedExpandedConceptIds,
  selectDeleteResolvedAnnotationErrors,
  selectFinalizedConceptIdsInCreateMode,
  selectResolvedReplaceModeByConceptId,
} from '../../../../state/selector';
import {
  addToFinalizedConceptIdsInCreateMode,
  addToResolvedAnnotationIdsInReplaceMode,
  addToResolvedExpandedConceptIds,
  clearAddEvidenceModeForConceptId,
  clearCreateAnnotationError,
  clearHighlightedTextSpans,
  clearPendingSpansForAnnotation,
  clearResolvedAllConceptsExpanded,
  removeFromAnnotationIdsInEditMode,
  removeFromResolvedExpandedConceptIds,
  setDeleteResolvedAnnotationError,
} from '../../../../state/supervisorSlice';
import { AnnotationWithAnnotatorName } from '../../../../types/types';

interface UseResolvedConceptCardProps {
  conceptId: string;
  annotations: AnnotationWithAnnotatorName[];
}

interface UseResolvedConceptCardResults {
  isExpanded: boolean;
  handleSetIsExpanded: (isExpanded: boolean) => void;
  annotationIdsInEditMode: string[];
  handleDeleteResolvedAnnotation: (
    e: React.MouseEvent<HTMLButtonElement>,
    annotationId: string
  ) => void;
  handleConfirmDelete: () => Promise<void>;
  showDeleteModal: boolean;
  setShowDeleteModal: (show: boolean) => void;
  handleDismissDeleteError: (annotationId: string) => void;
  isDeleteResolvedAnnotationLoading: boolean;
  deleteResolvedAnnotationErrors: Record<string, string | null>;
  shouldShowCreateButton: boolean;
  isInFinalizedCreateMode: boolean;
  handleStartFinalizedCreate: () => void;
  isInResolvedReplaceMode: boolean;
  resolvedAnnotationIdToDelete: string | null;
}
export const useResolvedConceptCard = ({
  conceptId,
  annotations,
}: UseResolvedConceptCardProps): UseResolvedConceptCardResults => {
  const { encounterId } = useParams();
  const { user } = useAuth();
  const { isReadOnly } = useSupervisorMode();
  const batchId = useAppSelector(selectBatchId);
  const { data: supervisorWorkspace } = useGetSupervisorWorkspaceQuery(
    { encounterId: encounterId as string, batchId: batchId as string },
    { skip: !user.isUser && !isReadOnly }
  );

  const resolvedExpandedConceptIds = useAppSelector(selectResolvedExpandedConceptIds);
  const annotationIdsInEditMode = useAppSelector(selectAnnotationIdsInEditMode);
  const resolvedAllConceptsExpanded = useAppSelector(selectResolvedAllConceptsExpanded);
  const finalizedConceptIdsInCreateMode = useAppSelector(selectFinalizedConceptIdsInCreateMode);
  const resolvedReplaceMode = useAppSelector(selectResolvedReplaceModeByConceptId(conceptId));
  const isExpanded = resolvedExpandedConceptIds.includes(conceptId);
  const isInFinalizedCreateMode = finalizedConceptIdsInCreateMode.includes(conceptId);
  const isInResolvedReplaceMode = resolvedReplaceMode !== null;
  const resolvedAnnotationIdToDelete = resolvedReplaceMode?.annotationIdToDelete ?? null;
  const dispatch = useAppDispatch();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteAnnotationId, setPendingDeleteAnnotationId] = useState<string | null>(null);

  const shouldShowCreateButton = useMemo(() => {
    if (!annotations || annotations.length === 0) return false;
    if (isInFinalizedCreateMode) return false;
    const hasAnnotationInEditMode = annotationIdsInEditMode.some((id) =>
      annotations.some((a) => a.annotationId === id)
    );
    if (hasAnnotationInEditMode) return false;
    return annotations.some((annotation) => {
      if (!annotation.attributes || annotation.attributes.length === 0) return false;
      return annotation.attributes.some(
        (attribute: AttributeValue) =>
          !BASE_ATTRIBUTE_NAMES.includes(attribute.name) &&
          ((attribute.value !== undefined && attribute.value !== null) || attribute.unknown)
      );
    });
  }, [annotations, annotationIdsInEditMode, isInFinalizedCreateMode]);

  const handleStartFinalizedCreate = () => {
    dispatch(addToFinalizedConceptIdsInCreateMode(conceptId));
  };

  const handleSetIsExpanded = (isExpanded: boolean) => {
    if (isExpanded) {
      dispatch(addToResolvedExpandedConceptIds(conceptId));
    } else {
      dispatch(removeFromResolvedExpandedConceptIds(conceptId));
    }
    dispatch(clearResolvedAllConceptsExpanded());
  };

  useEffect(() => {
    if (resolvedAllConceptsExpanded === true) {
      dispatch(addToResolvedExpandedConceptIds(conceptId));
    } else if (resolvedAllConceptsExpanded === false) {
      dispatch(removeFromResolvedExpandedConceptIds(conceptId));
    }
  }, [resolvedAllConceptsExpanded, conceptId, dispatch]);

  const [deleteResolvedAnnotation, { isLoading: isDeleteResolvedAnnotationLoading }] =
    useDeleteResolvedAnnotationMutation();

  const handleDeleteResolvedAnnotation = (
    e: React.MouseEvent<HTMLButtonElement>,
    annotationId: string
  ) => {
    e.preventDefault();

    if (!annotationId) {
      return;
    }

    const isLastAnnotation = annotations.length === 1;
    const conceptReview = supervisorWorkspace?.data.annotationsReview.find(
      (item) => item.conceptId === conceptId
    );
    const hasUnresolvedConflicts =
      conceptReview?.conflicts.some((conflict) => !conflict.resolved?.annotations?.length) ?? false;

    if (isLastAnnotation && !hasUnresolvedConflicts) {
      dispatch(
        addToResolvedAnnotationIdsInReplaceMode({ conceptId, annotationIdToDelete: annotationId })
      );
      return;
    }

    setPendingDeleteAnnotationId(annotationId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteAnnotationId) return;
    const annotationId = pendingDeleteAnnotationId;

    try {
      dispatch(setDeleteResolvedAnnotationError({ annotationId, error: null }));
      const result = await deleteResolvedAnnotation(annotationId).unwrap();

      if (result?.data?.deleted === true) {
        dispatch(clearPendingSpansForAnnotation(annotationId));
        dispatch(clearHighlightedTextSpans());
        dispatch(clearAddEvidenceModeForConceptId());
        dispatch(clearCreateAnnotationError());
        dispatch(removeFromAnnotationIdsInEditMode(annotationId));
      }
    } catch (err: any) {
      const message =
        err?.data?.detail ||
        err?.data?.message ||
        err?.data?.errorMessage ||
        err?.message ||
        'Failed to delete annotation';
      dispatch(setDeleteResolvedAnnotationError({ annotationId, error: message }));
    }
  };

  const deleteResolvedAnnotationErrors = useAppSelector(selectDeleteResolvedAnnotationErrors);

  const handleDismissDeleteError = (annotationId: string) => {
    dispatch(setDeleteResolvedAnnotationError({ annotationId, error: null }));
  };

  return {
    isExpanded,
    handleSetIsExpanded,
    annotationIdsInEditMode,
    handleDeleteResolvedAnnotation,
    handleConfirmDelete,
    showDeleteModal,
    setShowDeleteModal,
    handleDismissDeleteError,
    isDeleteResolvedAnnotationLoading,
    deleteResolvedAnnotationErrors,
    shouldShowCreateButton,
    isInFinalizedCreateMode,
    handleStartFinalizedCreate,
    isInResolvedReplaceMode,
    resolvedAnnotationIdToDelete,
  };
};
